import wretchRaw from "wretch";

import { getSession } from "../contexts/AuthContext";
import { notifyApiError } from "../utilities/apiErrorBus";

const API_ROOT = import.meta.env.VITE_API_ROOT;
const APP_HOST = import.meta.env.VITE_APP_APP_HOST;

export class ApiError extends Error {
  constructor(status, body, message) {
    const isUsableString =
      typeof message === "string" &&
      message.length > 0 &&
      message !== "[object Object]";
    const safeMessage = isUsableString
      ? message
      : `API request failed (${status})`;
    super(safeMessage);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

const createNotifiedApiError = (status, body, message) => {
  const apiError = new ApiError(status, body, message);
  notifyApiError(apiError);
  return apiError;
};

const throwApiError = (status) => (err) => {
  const body = err?.json ?? err?.text ?? null;
  const rawMessage = typeof err?.message === "string" ? err.message : null;
  throw createNotifiedApiError(status, body, rawMessage);
};

const hasValidId = (value) =>
  value !== undefined && value !== null && value !== "" && value !== "null";

const getApiHeaders = async (additionalHeaders = {}) => {
  const sessionHeaders = (await getSession()) ?? {};
  return { ...sessionHeaders, ...additionalHeaders };
};

const getAuthenticatedRequest = async (url, additionalHeaders) =>
  wretch(url).headers(await getApiHeaders(additionalHeaders));

const getErrorResponseBody = async (response) => {
  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  try {
    return await response.text();
  } catch {
    return null;
  }
};

const throwResponseApiError = async (response) => {
  const body = await getErrorResponseBody(response);
  let bodyMessage = null;

  if (typeof body === "string") {
    bodyMessage = body;
  } else if (Array.isArray(body?.errors) && typeof body.errors[0] === "string") {
    bodyMessage = body.errors[0];
  } else if (typeof body?.message === "string") {
    bodyMessage = body.message;
  }

  throw createNotifiedApiError(
    response.status,
    body,
    bodyMessage ?? response.statusText
  );
};

// Default wretch wrapper used by all data fetchers below. It throws an
// `ApiError` for 401/403/404 so React Query stops retrying and the central
// `ApiErrorHandler` can react. Auth endpoints (sign_in, password reset, etc.)
// should keep using `wretchRaw` directly so their bespoke handling is kept.
const wretch = (url) =>
  wretchRaw(url)
    .errorType("json")
    .catcher(401, throwApiError(401))
    .catcher(403, throwApiError(403))
    .catcher(404, throwApiError(404))
    .catcher(422, throwApiError(422));

export function apiUrl(path) {
  const url = API_ROOT + "/api/" + path;
  ////console.log("url", url);
  return url;
}

export const registerWithPassword = async ({
  email,
  password,
  confirmPassword,
  invitationToken,
}) => {
  return await wretchRaw(`${API_ROOT}/api/auth/password`)
    .put({
      email,
      password,
      invitation_token: invitationToken,
      password_confirmation: confirmPassword,
      accept_terms: true,
    })
    .json();
};

export const requestPasswordChange = async (email) => {
  ////console.log(APP_HOST + "reset-password?email=" + email);
  return await wretchRaw(`${API_ROOT}/api/auth/password`)
    .post({
      email: email,
      redirect_url: `${APP_HOST}reset-password?email=${email}`,
    })
    .json();
};

export const requestResendInvitation = async (email) => {
  return await wretchRaw(`${API_ROOT}/api/auth/request-invitation`)
    .post({
      email: email,
      redirect_url: `${APP_HOST}login`,
    })
    .json();
};

export const patchPassword = async ({ params, headers }) => {
  ////console.log(params, headers);
  return await wretchRaw(`${API_ROOT}/api/auth/password`)
    .headers(headers)
    .put(params)
    .json();
};

export const createHubSsoToken = async () => {
  const headers = await getSession();

  if (!headers) {
    const error = new Error("No session headers available for SSO token request");
    error.status = 401;
    throw error;
  }

  try {
    return await wretch(`${API_ROOT}/api/sso_tokens`)
      .headers(headers)
      .post({})
      .json();
  } catch (error) {
    if (error && typeof error.status === "number") {
      throw error;
    }

    // Normalize unexpected errors without a status code
    const wrappedError = new Error(error?.message || "Failed to create hub SSO token");
    wrappedError.status = 500;
    throw wrappedError;
  }
};

export const getEvents = async () => {
  const request = await getAuthenticatedRequest(`${API_ROOT}/api/events`);
  return await request.get().json();
};

export const getEvent = async ({ queryKey }) => {
  const { eventId } = queryKey[1];

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}`
  );
  return await request.get().json();
};

export const getInterests = async (eventIds = []) => {
  const validEventIds = eventIds.filter(hasValidId);

  if (validEventIds.length === 0) {
    return [];
  }

  const headers = await getApiHeaders();
  const responses = await Promise.all(
    validEventIds.map((eventId) =>
      wretch(`${API_ROOT}/api/events/${eventId}/interests`)
        .headers(headers)
        .get()
        .json()
    )
  );

  return responses.map((response) => response.data).flat();
};

export const getEventInterests = async ({ queryKey }) => {
  const { eventId } = queryKey[1];

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/interests`
  );
  return await request.get().json();
};

export const patchUserInterests = async (params) => {
  if (!hasValidId(params.eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${params.eventId}/interests`
  );
  return await request.post(params.payload).json();
};

export const getTimetable = async ({ queryKey }) => {
  const { eventId } = queryKey[1];

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/programs`
  );
  return await request.get().json();
};

export const getTimeslots = async ({ queryKey }) => {
  const { eventId } = queryKey[1];

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/timeslots`
  );
  return await request.get().json();
};

export const getAvailableMeetingSlots = async ({ queryKey }) => {
  const { eventId, meetingRequesterId, userId } = queryKey[1];

  if (
    !hasValidId(eventId) ||
    !hasValidId(meetingRequesterId) ||
    !hasValidId(userId)
  ) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/available_timeslots?requester_id=${meetingRequesterId}&receiver_id=${userId}`
  );
  return await request.get().json();
};

export const getUserMeetingQuota = async () => {
  const headers = await getSession();
  return await wretch(`${API_ROOT}/api/users/meeting_quota`)
    .headers(headers)
    .get()
    .json();
};

export const getEventParticipant = async ({ queryKey }) => {
  const { eventId, userId } = queryKey[1];

  if (!hasValidId(eventId) || !hasValidId(userId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/participants/${userId}`
  );
  return await request.get().json();
};
export const getEventParticipants = async ({ queryKey }) => {
  const { eventId } = queryKey[1];

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/participants`
  );
  return await request.get().json();
};

export const getEventCompanies = async ({ queryKey }) => {
  const { eventId } = queryKey[1];

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/companies`
  );
  return await request.get().json();
};

export const getEventPartners = async ({ queryKey }) => {
  const { eventId } = queryKey[1];

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/partners`
  );
  return await request.get().json();
};

export const getEventPartner = async ({ queryKey }) => {
  const { eventId, companyId } = queryKey[1];

  if (!hasValidId(eventId) || !hasValidId(companyId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/companies/${companyId}`
  );
  return await request.get().json();
};

export const getEventTimeslot = async ({ queryKey }) => {
  const { eventId, timeSlotId } = queryKey[1];

  if (!hasValidId(eventId) || !hasValidId(timeSlotId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/timeslots/${timeSlotId}`
  );
  return await request.get().json();
};

export const getMeetings = async ({ queryKey }) => {
  const { eventId } = queryKey[1];

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/meetings`
  );
  return await request.get().json();
};

export const createMeetingRequest = async (params) => {
  const { eventId, request } = params;

  if (!hasValidId(eventId)) {
    return;
  }

  const authenticatedRequest = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/meetings`
  );
  return await authenticatedRequest.post(request).json();
};

export const replyToMeetingRequest = async (params) => {
  const { eventId, meetingId, payload } = params;

  if (!hasValidId(eventId) || !hasValidId(meetingId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/meetings/${meetingId}`
  );
  return await request.put(payload).json();
};

export const setMeetingNoShow = async (params) => {
  const { eventId, id } = params;

  if (!hasValidId(eventId) || !hasValidId(id)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/meetings/${id}`
  );
  return await request.put({ no_show: true }).json();
};

export const setMeetingHappened = async (params) => {
  const { eventId, id } = params;

  if (!hasValidId(eventId) || !hasValidId(id)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/meetings/${id}`
  );
  return await request.put({ mark_happened: true }).json();
};

export const getMeetingMessages = async ({ queryKey }) => {
  const { eventId, meetingId } = queryKey[1];

  if (!hasValidId(eventId) || !hasValidId(meetingId)) {
    return { data: [] };
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/meetings/${meetingId}/messages`
  );
  return await request.get().json();
};

export const sendMeetingMessage = async (params) => {
  const { eventId, meetingId, body } = params;

  if (!hasValidId(eventId) || !hasValidId(meetingId)) {
    throw new Error("sendMeetingMessage: invalid eventId or meetingId");
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/meetings/${meetingId}/messages`
  );
  return await request.post({ body }).json();
};

export const markMessagesRead = async (params) => {
  const { eventId, meetingId, lastReadMessageId } = params;

  if (!hasValidId(eventId) || !hasValidId(meetingId)) {
    return null;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/meetings/${meetingId}/messages/read`
  );
  return await request
    .patch({ last_read_message_id: lastReadMessageId })
    .json();
};

export const getUserFavourites = async (params) => {
  const { eventId, userId } = params.queryKey[1];

  if (!hasValidId(eventId) || !hasValidId(userId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/favourites?user_id=${userId}`
  );
  return await request.get().json();
};

export const markAsFavourite = async (params) => {
  const { eventId, payload } = params;

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/favourites`
  );
  return await request.post(payload).json();
};

export const removeFromFavourites = async (params) => {
  const { eventId, payload } = params;

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/favourites?participant_id=${payload.participant_id}`
  );
  await request.delete();
};

export const getEventStreams = async ({ queryKey }) => {
  const { eventId } = queryKey[1];

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/video_streams`
  );
  return await request.get().json();
};

export const createEventLogger = async (params) => {
  const { eventId } = params;

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/event_loggers`
  );
  return await request.post(params.logs).json();
};

export const createContactRequest = async (params) => {
  const { eventId } = params;

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/contact_requests`
  );
  return await request.post(params).json();
};

export const getQrScanParticipant = async ({ queryKey }) => {
  const { scanId } = queryKey[1];

  if (!hasValidId(scanId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/qr_scan/${scanId}/scan_participant`
  );
  return await request.post().json();
};

export const qrScanParticipant = async (params) => {
  const { eventId, userId } = params;

  if (!hasValidId(eventId) || !hasValidId(userId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/participants/qr_scan`
  );
  return await request.post({ user_id: userId }).json();
};

export const createMeetingScore = async (params) => {
  const { eventId, meetingId, score } = params;

  if (!hasValidId(eventId) || !hasValidId(meetingId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/meetings/${meetingId}/reviews`
  );
  return await request.post({ score }).json();
};

export const createParticipantEndorsement = async (params) => {
  const { eventId, companyId, rateableId, score } = params;

  if (
    !hasValidId(eventId) ||
    !hasValidId(companyId) ||
    !hasValidId(rateableId)
  ) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/companies/${companyId}/participant_endorsements`
  );
  return await request.post({ score, rateable_id: rateableId }).json();
};

export const updateParticipantEndorsement = async (params) => {
  const { eventId, companyId, endorsementId, score } = params;

  if (!hasValidId(eventId) || !hasValidId(companyId) || !hasValidId(endorsementId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/companies/${companyId}/participant_endorsements/${endorsementId}`
  );
  return await request.put({ score }).json();
};

export const getParticipantEndorsements = async ({ queryKey }) => {
  const { eventId, companyId } = queryKey[1];

  if (!hasValidId(eventId) || !hasValidId(companyId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/companies/${companyId}/participant_endorsements`
  );
  return await request.get().json();
};

export const getUserEventLoggers = async ({ queryKey }) => {
  const [_key, { eventId }] = queryKey;

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/company_event_loggers`
  );
  return await request.get().json();
};

export const createPresentationScore = async (params) => {
  const { eventId } = params;

  if (!hasValidId(eventId)) {
    return;
  }

  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/presentation_scores`
  );
  return await request.post(params).json();
};

export const userPresentationScore = async (params) => {
  const { eventId, timeSlotId, userId } = params.queryKey[1];

  if (!hasValidId(eventId) || !hasValidId(timeSlotId) || !hasValidId(userId)) {
    return;
  }

  const queryParams = new URLSearchParams({
    app_time_slot_id: String(timeSlotId),
    user_id: String(userId),
  });
  const request = await getAuthenticatedRequest(
    `${API_ROOT}/api/events/${eventId}/presentation_scores/by_user?${queryParams.toString()}`
  );
  return await request.get().json();
};


export const updateUser = async (params) => {
  const { userId, payload } = params;

  if (!hasValidId(userId)) {
    return;
  }

  const isFormData = payload instanceof FormData;
  const url = `${API_ROOT}/api/users/${userId}`;

  if (isFormData) {
    const response = await fetch(url, {
      method: "PUT",
      headers: await getApiHeaders({
        Accept: "application/json",
      }),
      body: payload,
    });

    if (!response.ok) {
      await throwResponseApiError(response);
    }

    return await response.json();
  }

  const request = await getAuthenticatedRequest(url, {
    Accept: "application/json",
    "Content-Type": "application/json",
  });
  return await request.put(payload).json();
};